// Lógica de una fila de la Carga Académica (ver AcademicLoadRow.vue). Está aparte porque
// @vue/vue2-jest no compila <script setup>: así se prueba con un componente mínimo.
// Los valores se guardan donde vive el dato, igual que en el Plan de Estudio: créditos,
// textos y divisiones en la Asignatura Base si usa sus valores (use_matter_values), si no
// en la del perfil; semestre y pre-requisitos siempre en la del perfil; nombre en la base.
import { computed, getCurrentInstance } from "vue";
import * as names from "@/store/names";
import { toast } from "@/utils/utils";
import { pucvSctCredits, pucvWeeklyHours } from "@/utils/pucvCredits";
import {
  academicLoadTotals,
  periodicityLabel,
  periodicityOptions as buildPeriodicityOptions,
  presentialWeeklyHours,
  totalHoursFromWeekly,
  weeklyHours,
} from "@/utils/academicLoad";

export default function useAcademicLoadRow(props) {
  const proxy = getCurrentInstance().proxy;
  const store = proxy.$store;

  // Asignatura cuyos créditos, semanas, textos y divisiones se usan
  const source = computed(() =>
    props.egress_profile_matter.use_matter_values
      ? props.matter
      : props.egress_profile_matter
  );
  const editable = computed(
    () =>
      props.allows_crud &&
      !!source.value &&
      !source.value.is_closed &&
      !props.egress_profile_matter.is_closed
  );
  const weeks = computed(() => (source.value ? source.value.weeks : 0));
  const credits = computed(() =>
    source.value ? source.value.institutional_credits : 0
  );
  const sctCredits = computed(() => pucvSctCredits(credits.value));
  const levels = computed(() => {
    const amount = Math.max(
      props.semester_amount,
      props.egress_profile_matter.plan_level
    );
    return Array.from({ length: amount }, (_, index) => index + 1);
  });

  const presentialWeekly = computed(() =>
    presentialWeeklyHours(
      props.allocations,
      props.time_allocations,
      weeks.value,
      props.module_minutes
    )
  );
  const totals = computed(() =>
    academicLoadTotals(credits.value, presentialWeekly.value)
  );
  const autonomousIsNegative = computed(() => totals.value.autonomous_weekly < 0);
  const autonomousNegativeText = computed(() =>
    proxy.$t("studyPlan.academicLoad.autonomousNegative", {
      presential: presentialWeekly.value,
      weekly: pucvWeeklyHours(credits.value),
      credits: credits.value,
    })
  );
  const hoursHint = computed(() =>
    proxy.$t("studyPlan.academicLoad.hoursHint", { weeks: weeks.value })
  );
  const periodicityOptions = computed(() =>
    buildPeriodicityOptions(proxy.$t.bind(proxy))
  );

  function allocationValue(time_allocation, field) {
    const allocation = props.allocations[time_allocation.id];
    return allocation ? allocation[field] : null;
  }

  function weeklyHoursOf(time_allocation) {
    return weeklyHours(allocationValue(time_allocation, "hours"), weeks.value);
  }

  function classroomTypeName(time_allocation) {
    const classroom_type = props.classroom_types.find(
      (x) => x.id == allocationValue(time_allocation, "classroom_type")
    );
    return classroom_type ? classroom_type.name : "-";
  }

  function periodicityLabelOf(periodicity) {
    return periodicityLabel(proxy.$t.bind(proxy), periodicity);
  }

  function notifySaved() {
    toast(proxy.$t("studyPlan.academicLoad.changeMade"));
  }

  function patchEgressProfileMatter(item) {
    return store
      .dispatch(names.PATCH_EGRESS_PROFILE_MATTER, {
        egress_profile_matter_id: props.egress_profile_matter.id,
        item: item,
      })
      .then(notifySaved);
  }

  function patchMatter(item) {
    return store
      .dispatch(names.PATCH_MATTER, { matter_id: props.matter.id, item: item })
      .then(notifySaved);
  }

  function patchSource(item) {
    return props.egress_profile_matter.use_matter_values
      ? patchMatter(item)
      : patchEgressProfileMatter(item);
  }

  function patchPlanLevel(value) {
    const plan_level = parseInt(value);
    if (plan_level >= 1) patchEgressProfileMatter({ plan_level: plan_level });
  }

  // Código interno: siempre el de la Asignatura Base, aunque la institución use use_epm_code.
  // Misma regla que MatterForm: en mayúsculas y sin repetir el de otra Asignatura Base (ni
  // una sigla SAP) de la institución. Resuelve si se guardó, para revertir el input si no.
  function patchCode(value) {
    if (!props.matter) return Promise.resolve(false);
    const code = value.trim().toUpperCase();
    if (code == (props.matter.code || "")) return Promise.resolve(false);
    if (!code) return patchMatter({ code: code }).then(() => true);
    const params = new URLSearchParams({
      matter_code: code,
      matter_id: props.matter.id,
    });
    return proxy.$restful
      .Get(`/integrations/check_if_matter_code_exists/?${params}`)
      .then((used) => {
        if (used) {
          proxy.$swal.fire({
            icon: "warning",
            title: proxy.$t("studyPlan.academicLoad.codeAlreadyUsed", {
              code: code,
            }),
          });
          return false;
        }
        return patchMatter({ code: code }).then(() => true);
      });
  }

  // La Clave del Anexo 4 es siempre de la Asignatura Base, use o no sus valores el perfil
  function patchKey(value) {
    if (props.matter) patchMatter({ academic_load_key: value.trim() });
  }

  function patchName(value) {
    const name = value.trim();
    if (name) patchMatter({ name: name });
  }

  // Los SCT salen del crédito PUCV, así que se guardan junto con él
  function patchCredits(value) {
    const institutional_credits = Math.max(0, parseInt(value) || 0);
    patchSource({
      institutional_credits: institutional_credits,
      credits: pucvSctCredits(institutional_credits),
    });
  }

  function patchWeeklyHours(time_allocation, value) {
    saveAllocation(time_allocation, {
      hours: Math.round(totalHoursFromWeekly(value, weeks.value)),
    });
  }

  function patchQuota(time_allocation, value) {
    saveAllocation(time_allocation, {
      students_per_hour: Math.max(0, parseInt(value) || 0),
    });
  }

  // Creación en curso por División: un segundo cambio en la misma celda espera a que exista
  // la fila para hacer PATCH, en vez de intentar crearla dos veces (unique_together).
  // Las recién creadas se recuerdan aquí porque el prop allocations recién se actualiza
  // cuando el padre vuelve a renderizar.
  const pending_creations = {};
  const created_allocations = {};

  function saveAllocation(time_allocation, item) {
    const pending = pending_creations[time_allocation.id];
    if (pending) return pending.then(() => saveAllocation(time_allocation, item));
    const allocation =
      props.allocations[time_allocation.id] ||
      created_allocations[time_allocation.id];
    if (allocation) return patchAllocation(allocation, item).then(notifySaved);
    // Si falla, api.js ya mostró el error y el siguiente cambio vuelve a intentar crearla
    const request = createAllocation(time_allocation, item)
      .then((response) => {
        created_allocations[time_allocation.id] = response;
        notifySaved();
      })
      .catch(() => {})
      .finally(() => delete pending_creations[time_allocation.id]);
    pending_creations[time_allocation.id] = request;
    return request;
  }

  function patchAllocation(allocation, item) {
    if (props.egress_profile_matter.use_matter_values)
      return store.dispatch(names.PATCH_MATTER_TIME_ALLOCATION, {
        matter_time_allocation_id: allocation.id,
        item: item,
      });
    return store.dispatch(names.PATCH_EP_MATTER_TIME_ALLOCATION, {
      ep_matter_time_allocation_id: allocation.id,
      item: item,
    });
  }

  // Una División sin fila se crea con los valores por defecto de la División de tiempo,
  // igual que en el Plan de Estudio. Se usa $restful y no la acción POST del store porque
  // esta no rechaza si falla, y la creación pendiente de la celda quedaría colgada.
  function createAllocation(time_allocation, item) {
    const allocation = {
      time_allocation: time_allocation.id,
      counts_towards_credits: time_allocation.counts_towards_credits,
      attendance_requirement: time_allocation.attendance_requirement,
      students_per_hour: time_allocation.students_per_hour,
      classroom_type: time_allocation.classroom_type,
      periodicity: time_allocation.periodicity || "",
      hours: 0,
      ...item,
    };
    if (props.egress_profile_matter.use_matter_values)
      return proxy.$restful
        .Post("/mesh/matter-time-allocation/", {
          ...allocation,
          matter: props.matter.id,
        })
        .then((response) => {
          store.commit(names.MUTATE_MATTER_TIME_ALLOCATION, response);
          return response;
        });
    return proxy.$restful
      .Post("/mesh/egress-profile-matter-time-allocation/", {
        ...allocation,
        egress_profile_matter: props.egress_profile_matter.id,
      })
      .then((response) => {
        store.commit(names.MUTATE_EP_MATTER_TIME_ALLOCATION, response);
        return response;
      });
  }

  return {
    source,
    editable,
    credits,
    sctCredits,
    levels,
    totals,
    autonomousIsNegative,
    autonomousNegativeText,
    hoursHint,
    periodicityOptions,
    allocationValue,
    weeklyHoursOf,
    classroomTypeName,
    periodicityLabelOf,
    patchSource,
    patchPlanLevel,
    patchCode,
    patchKey,
    patchName,
    patchCredits,
    patchWeeklyHours,
    patchQuota,
    saveAllocation,
  };
}
